import React, { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button as CustomButton,
  Container,
  DataGridButton,
  Title,
  UserDeleteOptionsGreen,
  UserDeleteOptionsRed,
} from "./styles";
import { Alert, AlertColor } from "@mui/material";
import { UserPermissions } from "../../App";
import { Button, DialogContent, DialogTitle, FormControl, FormLabel, Input, Modal, ModalDialog, Stack, Textarea, Typography } from "@mui/joy";

interface Props {
  userPermissions: UserPermissions;
}
const FAQs: React.FC<Props> = ({ userPermissions }) => {

  const [faqDelete, setFaqDelete] = useState("");

  const [tableData, setTableData] = useState([]);

  const addFAQ = async (params: { question: string, answer: string }) => {
    return new Promise((resolve: any, reject: any) => {
      fetch("/api/v1/faq/add", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: params.question,
          answer: params.answer
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          if (data.success === true) {
            console.log("Created new FAQ!");
            setStatusMsg("New FAQ was created!");
            setStatusSeverity("success");
            setShowStatus(true);
            setTimeout(() => {
              setShowStatus(true);
            }, 5000);
            return resolve();
          } else {
            console.log("Not created: ", data.reason);
            if (data.reason === "Unkown Error")
              setStatusMsg(
                `FAQ was not created due to an unkown error. Refresh the page and try again.`
              );
            else
              setStatusMsg(`FAQ was not created: ${data.reason + ""}`);
            setStatusSeverity("error");
            setShowStatus(true);
            return reject(data.reason);
          }
        }).catch((err: any) => {
          return reject(err);
        });
    })
  }
  const deleteFAQ = async (params: { question: string }) => {
    return new Promise((resolve: any, reject: any) => {
      fetch("/api/v1/faq/remove", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: params.question,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          if (data.success === true) {
            console.log("Removed FAQ!");
            setStatusMsg("FAQ was removed.");
            setStatusSeverity("success");
            setShowStatus(true);
            setTimeout(() => {
              setShowStatus(true);
            }, 5000);
            return resolve();
          } else {
            console.log("Not removed: ", data.reason);
            if (data.reason === "Unkown Error")
              setStatusMsg(
                `FAQ was not removed due to an unkown error. Refresh the page and try again.`
              );
            else
              setStatusMsg(`FAQ was not removed: ${data.reason + ""}`);
            setStatusSeverity("error");
            setShowStatus(true);
            return reject(data.reason);
          }
        }).catch((err: any) => {
          return reject(err);
        });
    })
  }

  const fetchAllFAQs = () => {
    fetch("/api/v1/faq", {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status !== true) {
          console.log("Cannot fetch list of FAQs");
          return;
        }
        console.log(data.result);

        data = data.result.map((faq: { question: string; answer: string }) => {
          return {
            id: faq.question,
            answer: faq.answer,
          };
        });
        setTableData(data);
        setTableLoading(false);
      });
  };

  const [tableLoading, setTableLoading] = useState(false);
  useEffect(() => {
    setTableLoading(true);
    fetchAllFAQs();

    const interval = setInterval(() => {
      fetchAllFAQs();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      field: "id",
      headerName: "Question",
      width: 950,
    },
    {
      field: "action",
      headerName: "Action",
      width: 250,
      renderCell: (params: any) => (
        <span style={{}}>
          <DataGridButton
            onClick={() => {
              setShowFAQModalInformation({
                question: params.row.id,
                answer: params.row.answer
              });
            }}
          >
            View
          </DataGridButton>
          {faqDelete === params.row.id ? (
            <>
              Delete?{" "}
              <UserDeleteOptionsRed
                onClick={() => {
                  deleteFAQ({ question: faqDelete });
                  setFaqDelete("");
                }}
              >
                Yes
              </UserDeleteOptionsRed>{" "}
              <UserDeleteOptionsGreen
                onClick={() => {
                  setFaqDelete("");
                }}
              >
                No
              </UserDeleteOptionsGreen>
            </>
          ) : (
            <DataGridButton
              style={{ color: "#DB3A34", border: "2px solid #DB3A34" }}
              onClick={() => {
                setFaqDelete(params.row.id);
              }}
            >
              Delete
            </DataGridButton>
          )}
        </span>
      ),
    },
  ];

  //#region Create FAQ Modal
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  //#endregion
  //#region View FAQ Modal
  const [showFAQModalInformation, setShowFAQModalInformation] = React.useState<{ question?: string, answer?: string }>({});
  //#endregion

  const [showStatus, setShowStatus] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState("");
  const [statusSeverity, setStatusSeverity] =
    React.useState<AlertColor>("error");
  const handleCloseStatus = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowStatus(false);
  };

  return (
    <Container>
      <Title>
        Frequently Asked Questions{" "}
        {userPermissions.ORI.Admin && (
          <CustomButton
            onClick={() => {
              setShowCreateModal(true);
            }}
          >
            Add new
          </CustomButton>
        )}
      </Title>

      <div style={{ height: 500, width: "100%", paddingTop: "1rem" }}>
        <DataGrid
          style={{ fontWeight: 900, color: "rgba(55, 73, 87, 0.7)" }}
          rows={tableData}
          columns={columns}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          loading={tableLoading}
        />
      </div>

      <Snackbar
        open={showStatus}
        autoHideDuration={2000}
        onClose={handleCloseStatus}
      >
        <Alert
          onClose={handleCloseStatus}
          severity={statusSeverity}
          sx={{ width: "100%" }}
        >
          {statusMsg}
        </Alert>
      </Snackbar>

      {/* View FAQ Modal */}
      <Modal open={JSON.stringify(showFAQModalInformation) !== "{}"} onClose={() => setShowFAQModalInformation({})}>
        <ModalDialog>
          <Typography level="h1">Frequently Asked Question</Typography>
          <Typography level="h2" fontSize="xl" sx={{ mb: 0.5 }}>
            {showFAQModalInformation.question ?? ""}
          </Typography>
          <Typography>
            {showFAQModalInformation.answer ?? ""}
          </Typography>
        </ModalDialog>
      </Modal>

      {/* Create FAQ Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <ModalDialog>
          <DialogTitle>Create a new Frequently Asked Question</DialogTitle>
          <DialogContent>Please fill in the question and the answer.</DialogContent>
          <form
            onSubmit={async (event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();

              const formData = {
                // @ts-ignore
                question: event.currentTarget.elements.question.value,
                // @ts-ignore
                answer: event.currentTarget.elements.answer.value
              }
              await addFAQ(formData);

              setShowCreateModal(false);
            }}
          >
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Question</FormLabel>
                <Input autoFocus required name="question" />
              </FormControl>
              <FormControl>
                <FormLabel>Answer</FormLabel>
                <Textarea
                  required
                  color="neutral"
                  minRows={2}
                  size="lg"
                  variant="outlined"
                  name="answer"
                />
              </FormControl>
              <Button type="submit">Add</Button>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </Container>
  );
};

export default FAQs;
